// Import app config.
import AppConfig from '@config/app';
// Import app models.
import {
	ThirdPartyAuth,
	ThirdPartyAuthToken,
} from '@app/models/app';
// Import interfaces.
import {
	IAuthentication,
} from '@app/interfaces';
// Import interface parameters.
import {
	GetAccessTokenParameters,
	InitiateParameters,
	LogoutParameters,
	RefreshAccessTokenParameters,
	SearchAndLoadLoginSessionParameters,
} from '@app/interfaces/parameters';
// Import msal browser modules.
import {
	AccountInfo,
} from '@azure/msal-browser';

type SSOConfig = typeof AppConfig.sso_config;

export default class ThirdPartyAuthenticationService implements IAuthentication {

	private ssoConfig: SSOConfig;

	public constructor() {
		//Read the configuration for the SSO.
		this.ssoConfig = AppConfig.sso_config;
	}

	//#region interface methods

	/**
	 * Fetches the access token with the possible redirect.
	 * @param params
	 */
	public getAccessTokenWithRedirect(params: GetAccessTokenParameters): void {
		let accessToken: string | undefined = this.getThirdPartyAuthAccessToken();
		let isUserLoginExpired: boolean = this.isTokenExpired();
		let isUserLoggedIn: boolean = this.isLoggedIn();

		if (!isUserLoginExpired
			&& isUserLoggedIn
			&& accessToken !== undefined
			&& !String.isNullOrWhiteSpace(accessToken)) {
			params.getTokenSuccessCallback(accessToken);
		}
		else {
			// If the user is not logged in, then make sure to clear out any third party auth cookies.
			this.clearThirdPartyAuth();
			params.getTokenFailCallback();
		}
	}

	/**
	 * Force refreshes the lifetime of the token.
	 * @param params
	 */
	public refreshTokenWithRedirect(params: RefreshAccessTokenParameters): void {
		// We do not allow refreshing on third party tokens.
		this.clearThirdPartyAuth();
		params.refreshTokenFailCallback();
	}

	/**
	 * Searches and loads any existing login session.
	 * @param params
	 */
	public searchAndLoadExistingLogin(params: SearchAndLoadLoginSessionParameters): void {
		let accessToken: string | undefined = this.getThirdPartyAuthAccessToken();
		let isUserLoginExpired: boolean = this.isTokenExpired();
		let isUserLoggedIn: boolean = this.isLoggedIn();
		if (!isUserLoginExpired
			&& isUserLoggedIn
			&& accessToken !== undefined
			&& !String.isNullOrWhiteSpace(accessToken)) {
			params.searchAndLoadLoginSessionSuccessCallback(accessToken);
		}
		else {
			params.searchAndLoadLoginSessionFailCallback();
		}
	}

	/**
	 * Invokes a login redirect call. 
	 **/
	public login(): void {
		// Clear any third party auth cookies, we do not fetch tokens internally.
		this.clearThirdPartyAuth();
	}

	/**
	 * Initiates the login redirect process, if there is any.
	 * @param params
	 */
	public initiate(params: InitiateParameters): void {
		let accessToken: string | undefined = this.getThirdPartyAuthAccessToken();
		let isUserLoggedIn: boolean = this.isLoggedIn();

		if (accessToken !== undefined
			&& isUserLoggedIn
			&& params.finishLoginRedirectCallback !== undefined) {
			params.finishLoginRedirectCallback(accessToken);
		}
		else if (params.noLoginRedirectCallback !== undefined) {
			params.noLoginRedirectCallback();
		}
	}

	/**
	 * Logs the current authenticated user out of the application with parameters.
	 * @param params
	 */
	public logoutWithParameters(params: LogoutParameters): void {
		let postLogoutRedirectUri: string = this.ssoConfig.postLogoutRedirectUri.format(params.appV2LoggedOut.toString(), encodeURIComponent(params.returnToUrl));
		this.logout(postLogoutRedirectUri);
	}

	/**
	 * Logs the current authenticated user out of the application.
	 */
	public logout(redirectUrl: string): void {
		// Make sure any third party auth cookies are cleared out for logging out.
		this.clearThirdPartyAuth();
		document.location.href = redirectUrl
	}

	/**
	 *  Get the current authenticated user.
	 */
	public getUser(): AccountInfo | null {
		return this.getThirdPartyAuthTokenAccountInfo() ?? null;
	}

	/**
	 * Checks to see if the user is logged in. 
	 */
	public isLoggedIn(): boolean {
		return this.getUser() !== null;
	}

	//#endregion interface methods

	//#region helper methods

	private getThirdPartyAuth(): ThirdPartyAuth | undefined {
		let thirdPartyAuthStr: string | null = localStorage.getItem('thirdPartyAuth');
		let thirdPartyAuth: ThirdPartyAuth | undefined = thirdPartyAuthStr !== null ? JSON.parse(thirdPartyAuthStr) : undefined;
		return thirdPartyAuth;
	}

	private getThirdPartyAuthToken(): ThirdPartyAuthToken | undefined {
		let thirdPartyAuth: ThirdPartyAuth | undefined = this.getThirdPartyAuth();
		let thirdPartyAuthToken: ThirdPartyAuthToken | undefined = undefined;

		if (thirdPartyAuth !== undefined) {
			try {
				thirdPartyAuthToken = String.parseJwt<ThirdPartyAuthToken>(thirdPartyAuth.token);
			}
			catch (err) {
				console.error(`Unable to parse third party auth token: ${err}`);
			}
		}
		return thirdPartyAuthToken;
	}

	private getThirdPartyAuthTokenAccountInfo(): AccountInfo | undefined {
		let thirdPartyAuthToken: ThirdPartyAuthToken | undefined = this.getThirdPartyAuthToken()
		let accountInfo: AccountInfo | undefined = undefined;
		if (thirdPartyAuthToken !== undefined) {
			accountInfo = {
				homeAccountId: String.empty(),
				localAccountId: String.empty(),
				nativeAccountId: String.empty(),
				environment: String.empty(),
				tenantId: String.empty(),
				idToken: String.empty(),
				username: String.empty(),
				name: String.empty(),
				idTokenClaims: {
					aud: thirdPartyAuthToken.aud,
					emails: thirdPartyAuthToken.emails,
					exp: thirdPartyAuthToken.exp,
					name: thirdPartyAuthToken.name,
					iat: thirdPartyAuthToken.iat,
					idp: thirdPartyAuthToken.idp,
					iss: thirdPartyAuthToken.iss,
					nbf: thirdPartyAuthToken.nbf,
					oid: thirdPartyAuthToken.oid,
					sub: thirdPartyAuthToken.sub,
					ver: thirdPartyAuthToken.ver,
				}
			};
		}
		return accountInfo;
	}

	private isTokenExpired(): boolean {
		let user: AccountInfo | null = this.getUser();
		let isTokenExpired: boolean = true;
		if (user?.idTokenClaims?.exp !== undefined) {
			let expirationDateInMilliseconds: number = user.idTokenClaims.exp * 1000;
			let timeLeftInMilliseconds: number = expirationDateInMilliseconds - Date.now();
			isTokenExpired = timeLeftInMilliseconds <= 0;
		}
		return isTokenExpired;
	}

	private getThirdPartyAuthAccessToken(): string | undefined {
		return this.getThirdPartyAuth()?.token ?? undefined;
	}

	private clearThirdPartyAuth(): void {
		localStorage.removeItem('thirdPartyAuth');
	}

	//#endregion helper methods
}
